//
// Event structure.
// An event is divided into four document types:
//
// 1. EventDoc: (event) The main event document (info about the event) (public)
// 2. EventRegDoc (event/regs) (one pr subevent - public): Registration info for individual subevents
// 5. Maybe : EventUnlistedDoc -- unpublised event lists?
// 4. EventResultDoc: (event/results) Results pr subevent (public)
//
import { DateTime } from 'luxon';
import { hasEditPermission } from './role.js';
import { toNumber } from './utils/utils.js';
import { applyDiscount, buildDiscountContext, findBestDiscount, findOfferedPriceOption, resolvePriceOptions } from './discount.js';
import { isWithinValidity, parseSiteDateTime } from './validity.js';
import { activeGroupIds, activeGroups, isInAnyGroup } from './sitedata/group.js';
import { emptyUnitPrice } from './ticket.js';
var EventFormFieldType;
(function (EventFormFieldType) {
    EventFormFieldType["string"] = "string";
    EventFormFieldType["text"] = "text";
    EventFormFieldType["number"] = "number";
    EventFormFieldType["boolean"] = "boolean";
    EventFormFieldType["date"] = "date";
    EventFormFieldType["select"] = "select";
    EventFormFieldType["multi"] = "multi-select";
})(EventFormFieldType || (EventFormFieldType = {}));
var EventFormAppliesTo;
(function (EventFormAppliesTo) {
    EventFormAppliesTo["order"] = "order";
    EventFormAppliesTo["ticket"] = "ticket";
    EventFormAppliesTo["handler"] = "handler";
    EventFormAppliesTo["dog"] = "dog";
    EventFormAppliesTo["equipage"] = "equipage";
})(EventFormAppliesTo || (EventFormAppliesTo = {}));
// Who may read the ANSWERS to a form field — and, because the two were never
// usefully separate, who gets a column for it in the start list. The old
// `visible` flag ("Vis som egen kolonne i listen") was a second, independent
// switch that decided columns alone, which meant a participant could be allowed
// to read an answer and still not be shown it, and a reader who was not allowed
// the answer still got an empty column with the question as its header. Columns
// are now derived from this ladder: you get a column exactly when you are
// entitled to what is in it. Defaults to `organizer` everywhere (see
// toEventFormDef): fields authored before this existed stay private until an
// organizer opts them in. See F-RULES-01.
//
// A ladder, widest last. `participant` exists because most of what a club
// legitimately shares — who else is on the course, which class someone entered,
// what level their dog is at — is meant for the people attending, and going
// straight from "organizer only" to "anyone on the internet, logged out" is too
// big a step to be the only choice on offer.
var EventFormAudience;
(function (EventFormAudience) {
    EventFormAudience["organizer"] = "organizer";
    EventFormAudience["participant"] = "participant";
    EventFormAudience["public"] = "public"; // Anyone at all, including logged out
})(EventFormAudience || (EventFormAudience = {}));
// Widest audience the viewer belongs to. Same enum on purpose: a field is shown
// when the viewer's standing reaches the field's requirement, which is one
// comparison rather than a matrix.
const audienceRank = (audience) => {
    switch (audience) {
        case EventFormAudience.public:
            return 0;
        case EventFormAudience.participant:
            return 1;
        case EventFormAudience.organizer:
            return 2;
    }
};
const audienceAllows = (field, viewer) => {
    return audienceRank(viewer) >= audienceRank(field);
};
// The widest tier a viewer stands in on one event. Bookie-or-higher on the
// hosting site is the organizer tier whether or not they also hold a ticket;
// `isParticipant` is the endpoint's own verdict (a booked ticket owned by the
// caller), not something the client decides for itself.
const viewerAudience = (role, isParticipant) => {
    if (hasEditPermission(role))
        return EventFormAudience.organizer;
    if (isParticipant)
        return EventFormAudience.participant;
    return EventFormAudience.public;
};
const signUpTotalPrice = (allTickets) => {
    let total = 0;
    for (const ticket of allTickets) {
        total += toNumber(ticket.unitPrice.finalPrice);
    }
    return total;
};
/**
 * The price of one sub-event ticket, optionally at a price the buyer picked
 * for themselves (student, honnor, ...).
 *
 * This is the single definition of what a sign-up costs: the client uses it to
 * fill the cart and the backend uses it to re-derive the price at reserve time
 * (carts are client-writable, so the amount in the cart is only a claim - see
 * F-PAY-01 in docs/trackers/security_review.md). A claimed option that the sub-event
 * does not offer resolves to nothing and the buyer pays the ordinary price.
 *
 * Discounts the buyer must qualify for — group, club code, punch card — are
 * applied too, from `EventSub.price.discount`. They compete with a chosen price
 * option in one cascade rather than stacking; see {@link eventSubUnitPrice}.
 */
/**
 * The eligibility context for event pricing, built the one way.
 *
 * Both the client (writing the cart) and the reserve guard (re-deriving it) go
 * through this, so neither can assemble a subtly different context and make the
 * guard reject a legitimate cart. Group slugs are resolved from the site's
 * definitions rather than the membership rows — see activeGroups.
 */
const eventDiscountContext = (siteData, user, clubCodes, now) => {
    // Read in the site's zone, Gyldig til including its whole day (ADR 0014)
    const validClubCodes = clubCodes.filter((code) => isWithinValidity(now, code, siteData.settings?.timezone));
    return buildDiscountContext({
        uid: user?.uid ?? '',
        punchCards: (user?.punchCards ?? []).map((p) => ({
            id: p.id,
            slug: p.slug
        })),
        clubCodes: validClubCodes.map((c) => ({ id: c.id, slug: c.slug })),
        groups: activeGroups(user, siteData.groups, now)
    });
};
/**
 * The price one sub-event costs this buyer.
 *
 * The single pricing function for events: the client writes the cart from it and
 * the reserve guard re-derives from it, so the two cannot disagree (F-PAY-01).
 * Both a self-selected price option and the sub-event's own `discount` compete
 * in one cascade — lowest final price wins, nothing stacks, and on a tie the
 * buyer's explicit choice is recorded. See docs/plans/price_options.md and
 * docs/plans/groups.md.
 *
 * `context` is required rather than optional on purpose. If one of the two call
 * sites could omit it they would price differently, and the guard would reject
 * a legitimately discounted cart — so the compiler asks both.
 *
 * There is no blanket per-code event discount to apply. `ClubCode` used to
 * carry `generalDiscount.eventDiscount`; it was authorable, read by nothing, and
 * has been removed rather than switched on — see `config/codes.ts`. A `club:`
 * pattern on `EventSub.price.discount` expresses the same intent per sub-event
 * and is applied here.
 */
const eventSubUnitPrice = (sub, priceOptions, context, selectedOptionId) => {
    const basePrice = toNumber(sub.price.normalPrice);
    const unitPrice = emptyUnitPrice();
    unitPrice.basePrice = basePrice;
    unitPrice.finalPrice = basePrice;
    // Nothing to discount, and no punch to waste. Matches live.pricer.
    if (basePrice === 0)
        return unitPrice;
    const option = findOfferedPriceOption(priceOptions, sub.price.options, selectedOptionId);
    const configs = sub.price.discount
        ? [sub.price.discount]
        : [];
    const best = findBestDiscount(basePrice, configs, context, option);
    if (!best)
        return unitPrice;
    unitPrice.finalPrice = applyDiscount(basePrice, best);
    unitPrice.discount = best;
    return unitPrice;
};
/**
 * The price options a sub-event offers, each with the price it yields for this
 * buyer. Used to render the picker; the ordinary price is not included.
 *
 * `unitPrice` is run through the whole cascade rather than just the option's
 * own discount, so a group discount the buyer already qualifies for is visible
 * in the picker instead of only surfacing in the cart. It is the same shape the
 * ticket is written with, and mirrors LivePricer.findPriceOptions for slots.
 */
const eventSubPriceOptions = (sub, priceOptions, context) => {
    const offered = resolvePriceOptions(priceOptions, sub.price.options);
    if (offered.length === 0)
        return [];
    // Nothing to choose when the sub-event already costs this buyer nothing -
    // a free class, or a discount that has taken the price to zero.
    if (eventSubUnitPrice(sub, priceOptions, context).finalPrice === 0)
        return [];
    return offered.map((option) => ({
        option,
        unitPrice: eventSubUnitPrice(sub, priceOptions, context, option.id)
    }));
};
const toEventTicket = (raw) => {
    return {
        ticketId: raw.ticketId ?? '', // Unique id for the ticket was added january 2026
        partaker: raw.partaker ?? {},
        orderId: raw.orderId,
        ownerUid: raw.ownerUid,
        status: raw.status ?? EventTicketStatus.unknown,
        reservedAt: raw.reservedAt ?? '',
        extra: raw.extra ?? {},
        orderExtra: raw.orderExtra ?? {}
    };
};
const emptyEventReg = () => {
    return {
        eventId: '',
        eventSubId: '',
        tickets: []
    };
};
const eventTicketsBookedOrReserved = (allTickets, now) => {
    return allTickets.filter((t) => {
        if (t.status === EventTicketStatus.reserved) {
            const reservedAt = DateTime.fromISO(t.reservedAt);
            return reservedAt.plus({ minutes: 10 }) >= now;
        }
        return t.status === EventTicketStatus.booked;
    });
};
const toEventRegDoc = (raw) => {
    if (!raw)
        return emptyEventReg();
    return {
        eventId: raw.eventId ?? '',
        eventSubId: raw.eventSubId ?? '',
        tickets: Array.isArray(raw.tickets) ? raw.tickets.map(toEventTicket) : []
    };
};
var EventTicketStatus;
(function (EventTicketStatus) {
    EventTicketStatus["reserved"] = "reserved";
    EventTicketStatus["booked"] = "booked";
    EventTicketStatus["unknown"] = "unknown";
})(EventTicketStatus || (EventTicketStatus = {}));
var EventPartakerType;
(function (EventPartakerType) {
    EventPartakerType["handler"] = "handler";
    EventPartakerType["dog"] = "dog";
    EventPartakerType["equipage"] = "equipage";
})(EventPartakerType || (EventPartakerType = {}));
const emptyEventSub = () => {
    return {
        id: '',
        name: '',
        tickets: {
            total: 0,
            reserved: 0
        },
        partaker: EventPartakerType.equipage,
        price: {
            normalPrice: 0
        }
    };
};
var EventTypes;
(function (EventTypes) {
    EventTypes["other"] = "other";
    EventTypes["course"] = "course";
    EventTypes["group"] = "group";
    EventTypes["dugnad"] = "dugnad";
    EventTypes["herding"] = "herding";
    EventTypes["agility"] = "agility";
    EventTypes["obedience"] = "obedience";
    EventTypes["rally"] = "rally";
})(EventTypes || (EventTypes = {}));
var EventMessageVisibility;
(function (EventMessageVisibility) {
    EventMessageVisibility["public"] = "public";
    EventMessageVisibility["private"] = "private";
})(EventMessageVisibility || (EventMessageVisibility = {}));
const emptyEventMessageReadState = () => {
    return { readMessageIds: [], updatedAt: '' };
};
const toEventMessageReadState = (raw) => {
    const result = emptyEventMessageReadState();
    if (!raw)
        return result;
    result.readMessageIds = Array.isArray(raw.readMessageIds)
        ? raw.readMessageIds.filter((id) => typeof id === 'string')
        : [];
    result.updatedAt = raw.updatedAt ?? '';
    return result;
};
const emptyEvent = () => {
    return {
        id: '',
        type: EventTypes.course,
        name: '',
        tagLine: '',
        location: '',
        info: '',
        freeText: '',
        publisedAt: '',
        published: false,
        hidden: false,
        imageUrl: '',
        discount: {
            enabled: false,
            code: '',
            groups: [],
            label: ''
        },
        signup: {
            code: '',
            groups: [],
            from: '',
            to: '',
            lockedAt: '',
            idRequired: false,
            publishedAt: '',
            published: false
        },
        times: {
            from: '',
            to: '',
            spaceTime: '',
            activityName: ''
        },
        form: [],
        feedbackForm: [],
        feedbackAutoSend: true,
        feedbackSentAt: '',
        feedbackSubIds: [],
        subs: []
    };
};
const emptyEventFeedback = () => {
    return {
        token: '',
        ownerUid: '',
        sentAt: '',
        expiresAt: '',
        submittedAt: '',
        isAnonymous: false,
        responses: {}
    };
};
const emptyEventMessage = () => {
    return {
        id: '',
        title: '',
        body: '',
        visibility: EventMessageVisibility.public,
        authorUid: '',
        authorName: '',
        createdAt: '',
        editedAt: '',
        emailSentAt: '',
        emailSendCount: 0
    };
};
/** Group ids off an untrusted document — anything not a non-empty string goes. */
const toGroupIdList = (raw) => {
    if (!Array.isArray(raw))
        return [];
    return raw.filter((id) => typeof id === 'string' && id !== '');
};
const toEventSub = (raw) => {
    if (!raw)
        return emptyEventSub();
    const result = {
        id: raw.id,
        name: raw.name,
        tickets: {
            total: raw.tickets?.total ?? 0,
            reserved: raw.tickets?.reserved ?? 0
        },
        partaker: raw.partaker ?? EventPartakerType.handler,
        price: {
            normalPrice: raw.price?.normalPrice ?? 0
        }
    };
    // Only carried when the sub-event actually opts in, so round-tripping an
    // event document does not add an empty field to every existing sub-event
    if (typeof raw.price?.options === 'string' && raw.price.options !== '') {
        result.price.options = raw.price.options;
    }
    // Same treatment for the group restriction, and for the same reason
    const restrictedTo = toGroupIdList(raw.restrictedTo);
    if (restrictedTo.length > 0) {
        result.restrictedTo = restrictedTo;
    }
    // Parse new discount format
    if (raw.price?.discount && typeof raw.price.discount === 'object') {
        result.price.discount = raw.price.discount;
    }
    return result;
};
const toEventFormDef = (raw) => {
    return {
        order: raw.order ?? 0,
        key: raw.key ?? raw.id ?? '',
        label: raw.label ?? '',
        type: raw.type ?? EventFormFieldType.string,
        values: raw.values ?? [],
        required: raw.required ?? false,
        appliesTo: raw.appliesTo ?? EventFormAppliesTo.ticket,
        // Fail closed: only the two literal strings widen a field. Anything else —
        // absent, misspelled, a boolean left over from an older editor — reads as
        // `organizer`, which is the intended default (F-RULES-01).
        audience: raw.audience === EventFormAudience.public
            ? EventFormAudience.public
            : raw.audience === EventFormAudience.participant
                ? EventFormAudience.participant
                : EventFormAudience.organizer
    };
};
const toEventDoc = (raw) => {
    const result = emptyEvent();
    if (!raw)
        return result;
    result.id = raw.id ?? '';
    result.type = raw.type ?? 'other';
    result.name = raw.name ?? '';
    result.imageUrl = raw.imageUrl ?? '';
    result.tagLine = raw.tagLine ?? '';
    result.location = raw.location ?? '';
    result.info = raw.info ?? '';
    result.freeText = raw.freeText ?? '';
    result.publisedAt = raw.publisedAt ?? '';
    result.published = raw.published || false;
    result.hidden = raw.hidden || false;
    result.discount.enabled = raw.discount?.enabled || false;
    result.discount.code = raw.discount?.code ?? '';
    result.discount.groups = toGroupIdList(raw.discount?.groups);
    // Written as `label`; `discountLabel` is the name it used to be read
    // under, kept so either reads (D22)
    result.discount.label =
        raw.discount?.label ?? raw.discount?.discountLabel ?? '';
    result.signup.code = raw.signup?.code ?? '';
    result.signup.groups = toGroupIdList(raw.signup?.groups);
    result.signup.from = raw.signup?.from ?? '';
    result.signup.to = raw.signup?.to ?? '';
    result.signup.lockedAt = raw.signup?.lockedAt ?? '';
    result.signup.idRequired = raw.signup?.idRequired || false;
    result.signup.publishedAt = raw.signup?.publishedAt ?? '';
    result.signup.published = raw.signup?.published || false;
    result.times.from = raw.times?.from ?? '';
    result.times.to = raw.times?.to ?? '';
    result.times.spaceTime = raw.times?.spaceTime ?? '';
    result.times.activityName = raw.times?.activityName ?? '';
    if (Array.isArray(raw.form)) {
        result.form = raw.form.map(toEventFormDef);
    }
    else {
        result.form = [];
    }
    if (Array.isArray(raw.feedbackForm)) {
        result.feedbackForm = raw.feedbackForm.map(toEventFormDef);
    }
    else {
        result.feedbackForm = [];
    }
    result.feedbackAutoSend = raw.feedbackAutoSend !== false;
    result.feedbackSentAt = raw.feedbackSentAt ?? '';
    result.feedbackSubIds = Array.isArray(raw.feedbackSubIds)
        ? raw.feedbackSubIds.filter((id) => typeof id === 'string')
        : [];
    if (Array.isArray(raw.subs)) {
        result.subs = raw.subs.map(toEventSub);
    }
    else {
        result.subs = [];
    }
    return result;
};
const toEventFeedbackDoc = (raw) => {
    const result = emptyEventFeedback();
    if (!raw)
        return result;
    result.token = raw.token ?? '';
    result.ownerUid = raw.ownerUid ?? '';
    result.sentAt = raw.sentAt ?? '';
    result.expiresAt = raw.expiresAt ?? '';
    result.submittedAt = raw.submittedAt ?? '';
    result.isAnonymous = raw.isAnonymous === true;
    result.responses =
        raw.responses && typeof raw.responses === 'object' ? raw.responses : {};
    return result;
};
const toEventMessageDoc = (raw) => {
    const result = emptyEventMessage();
    if (!raw)
        return result;
    result.id = raw.id ?? '';
    result.title = raw.title ?? '';
    result.body = raw.body ?? '';
    result.visibility =
        raw.visibility === EventMessageVisibility.private
            ? EventMessageVisibility.private
            : EventMessageVisibility.public;
    result.authorUid = raw.authorUid ?? '';
    result.authorName = raw.authorName ?? '';
    result.createdAt = raw.createdAt ?? '';
    result.editedAt = raw.editedAt ?? '';
    result.emailSentAt = raw.emailSentAt ?? '';
    result.emailSendCount =
        typeof raw.emailSendCount === 'number' ? raw.emailSendCount : 0;
    return result;
};
/**
 * Is signup open now? Event times are stored as site wall-clock times (often
 * without an offset), so they are read in the site's zone - the same reading
 * in the browser and on the server (ADR 0014, D116).
 */
const eventBookingOpen = (event, role, codes, nowdt, user, timezone) => {
    if (hasEditPermission(role))
        return [true, ''];
    // Check if signup period has ended
    const signupTo = parseSiteDateTime(event.signup.to ?? event.times.to ?? '', timezone);
    if (signupTo && signupTo < nowdt)
        return [false, 'Påmelding er stengt'];
    // Check if signup period has started
    const signupFrom = parseSiteDateTime(event.signup.from ?? '', timezone);
    if (signupFrom) {
        if (signupFrom > nowdt) {
            for (const code of codes) {
                if (`${code.id}` === event.signup.code)
                    return [true, ''];
            }
            // A group named on signup.groups gets in early, same as holding the code
            // — but without anything to type. An empty list means nobody does, so it
            // must not read as "unrestricted" the way `restrictedTo` does.
            const earlyGroups = event.signup.groups ?? [];
            if (earlyGroups.length > 0) {
                const mine = activeGroupIds(user, nowdt);
                if (earlyGroups.some((groupId) => mine.includes(groupId))) {
                    return [true, ''];
                }
            }
            return [false, 'Påmelding har ikke åpnet'];
        }
    }
    return [true, ''];
};
/**
 * Are the event's tickets locked for editing (signup.lockedAt has passed)?
 * The lock time is a site wall-clock time, read in the site's zone (D116).
 */
const eventTicketsLocked = (event, nowdt, timezone) => {
    const lockedAt = parseSiteDateTime(event.signup.lockedAt, timezone);
    return !!lockedAt && nowdt >= lockedAt;
};
/**
 * May this user sign up for this subevent?
 *
 * Separate from {@link eventBookingOpen} because the gate is per sub: one
 * course can carry a members-only class and an open one. Empty `restrictedTo`
 * is open to everyone; a list naming only deleted groups fails closed.
 */
const eventSubOpenForUser = (sub, role, nowdt, user) => {
    if (hasEditPermission(role))
        return [true, ''];
    if (isInAnyGroup(user, sub.restrictedTo, nowdt))
        return [true, ''];
    return [false, 'Denne påmeldingen er kun for en egen gruppe'];
};
// Empty/absent dates parse to an Invalid DateTime, and every comparison on an
// Invalid DateTime is false - so "" reads as "not published", which is what the
// organizer UI has always meant by it.
const eventIsPublished = (event, now) => {
    if (event.published)
        return true;
    return DateTime.fromISO(event.publisedAt) < now;
};
const eventSignupsArePublished = (event, now) => {
    if (event.signup.published)
        return true;
    return DateTime.fromISO(event.signup.publishedAt) < now;
};
//
// Whether the start list may be shown to someone who is NOT bookie-or-higher.
// This lived only in apps/app/src/site/events/eventUtils.ts and was therefore
// enforced nowhere (F-RULES-01); it is here so the partakers endpoint and the
// frontend answer the question the same way. The two-month cutoff is the reason
// this cannot be a Firestore rule: times.to is an ISO *string*, and rules cannot
// parse one into a timestamp.
//
const eventStartListIsPublic = (event, now) => {
    if (!eventIsPublished(event, now))
        return false;
    if (!eventSignupsArePublished(event, now))
        return false;
    const endsAt = DateTime.fromISO(event.times.to);
    if (endsAt.isValid && endsAt < now.minus({ months: 2 }))
        return false;
    return true;
};
// Answers survive only if the viewer's standing reaches the field's audience.
// Keys with no matching field definition are dropped: an answer whose question
// has been deleted from the event has no audience to check against, so it fails
// closed.
const answersForAudience = (answers, form, viewer) => {
    const result = {};
    if (!answers)
        return result;
    for (const [key, value] of Object.entries(answers)) {
        const def = form.find((f) => f.key === key);
        if (def && audienceAllows(def.audience, viewer)) {
            result[key] = value;
        }
    }
    return result;
};
const toEventTicketForAudience = (ticket, form, viewer) => {
    return {
        ticketId: ticket.ticketId,
        partakerId: ticket.partaker?.partakerId ?? '',
        handlerName: ticket.partaker?.handlerName ?? '',
        dogName: ticket.partaker?.dogName ?? '',
        extra: {
            ...answersForAudience(ticket.orderExtra, form, viewer),
            ...answersForAudience(ticket.extra, form, viewer)
        }
    };
};
// Reserved tickets are excluded, not just redacted: a reservation is somebody
// mid-checkout, and a public endpoint that lists them leaks in-flight purchases
// (and, for a nearly-full course, who is about to take the last place). The
// organizer grid still shows them - it reads the documents directly.
const toEventRegForAudience = (reg, form, viewer) => {
    return {
        eventId: reg.eventId,
        eventSubId: reg.eventSubId,
        tickets: reg.tickets
            .filter((t) => t.status === EventTicketStatus.booked)
            .map((t) => toEventTicketForAudience(t, form, viewer))
    };
};
// Whether `uid` holds a booked ticket on this event, i.e. qualifies for
// EventFormAudience.participant. Same rule as fetchEventParticipantUids in
// apps/functions (booked tickets with an ownerUid, across all sub-events); this
// form takes registrations already in hand so the endpoint does not read the
// same collection twice.
const isEventParticipant = (regs, uid) => {
    if (!uid)
        return false;
    return regs.some((reg) => reg.tickets.some((t) => t.status === EventTicketStatus.booked && t.ownerUid === uid));
};
/**
 * Seats left on a sub-event. Antall plasser is a capacity, not a limit, so 0
 * means full (ADR 0013), the same as the server's reserve check. `taken`
 * defaults to the stored reserved count; callers that count tickets
 * themselves pass their own figure.
 */
const eventSubSeatsLeft = (sub, taken = toNumber(sub.tickets?.reserved, 0)) => Math.max(0, toNumber(sub.tickets?.total, 0) - taken);
const eventSubSoldOut = (sub, taken) => eventSubSeatsLeft(sub, taken) === 0;
/** An event is sold out when every sub-event is (D20) */
const eventSoldOut = (event) => event.subs.every((sub) => eventSubSoldOut(sub));
const eventSignupSummary = (event, sub, partaker) => {
    const name = event.subs.length > 1 ? `${event.name}: ${sub.name}` : event.name;
    const par = partaker.dogName ? ` med ${partaker.dogName}` : '';
    return `${name} ${par}`;
};
export { EventFormAppliesTo, EventFormAudience, EventFormFieldType, toEventTicketForAudience, toEventRegForAudience, isEventParticipant, audienceAllows, viewerAudience, eventIsPublished, eventSignupsArePublished, eventStartListIsPublic, emptyEvent, emptyEventSub, emptyEventMessage, emptyEventMessageReadState, emptyEventFeedback, eventSignupSummary, toEventDoc, toEventRegDoc, toEventSub, toEventMessageDoc, toEventMessageReadState, toEventFeedbackDoc, eventSubPriceOptions, eventDiscountContext, eventSubUnitPrice, signUpTotalPrice, EventTicketStatus, eventBookingOpen, eventTicketsLocked, eventSubOpenForUser, EventPartakerType, EventTypes, EventMessageVisibility, eventSoldOut, eventSubSeatsLeft, eventSubSoldOut, eventTicketsBookedOrReserved };
//# sourceMappingURL=event.js.map